import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChrome,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaYoutube,
} from "react-icons/fa";
import Tilt from "react-parallax-tilt";
import "./ProjectDetail.css";
import { Helmet } from "react-helmet-async";

const ProjectDetail = ({ project, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  React.useEffect(() => {
    const overlay = document.querySelector(".project-detail-overlay");
    if (overlay) {
      overlay.scrollTop = 0;
    }
  }, [project]);

  const projectData = {
    iplay: {
      title: "I-PLAY",
      description: "인하대학교 I-Class 강의 자동 플레이어",
      mainImage: "/imgs/I-Play.png",
      slideImages: [
        "/imgs/I-Play/001.png",
        "/imgs/I-Play/002.png",
        "/imgs/I-Play/003.png",
        "/imgs/I-Play/004.png",
      ],
      features: [
        "I-Class 강의 자동 재생",
        "재생목록 기능으로 여러 강의 연속 재생",
        "더 간편하게 수강완료를 확인",
        "재생 중 전체 음소거 기능",
      ],
      downloadLinks: {
        chrome:
          "https://chromewebstore.google.com/detail/ghijcjpcgppajgljcdpddjkmeoonphcm",
        youtube: "https://www.youtube.com/watch?v=18eM2xUyiTA",
      },
      additionalInfo:
        "인하대학교 학생들의 온라인 학습을 더욱 편리하게 만들어주는 필수 도구입니다.",
    },
    inotice: {
      title: "I-NOTICE",
      description: "인하대학교 공지사항 알림 서비스",
      mainImage: "/imgs/I-Notice.png",
      slideImages: [
        "/imgs/I-Notice/001.png",
        "/imgs/I-Notice/002.png",
        "/imgs/I-Notice/003.png",
        "/imgs/I-Notice/004.png",
        "/imgs/I-Notice/005.png",
        "/imgs/I-Notice/006.png",
        "/imgs/I-Notice/007.png",
        "/imgs/I-Notice/008.png",
      ],
      features: [
        "공지사항 모아보기",
        "원하는 학과 설정",
        "중요 공지 즐겨찾기",
        "개인 테마 설정",
      ],
      downloadLinks: {
        chrome:
          "https://chromewebstore.google.com/detail/lmcobmplblbjldbpjhcppnoklgcmjfal",
      },
      additionalInfo:
        "인하대학교의 중요한 소식을 놓치지 않도록 도와주는 공지사항 모아보기  서비스입니다.",
    },
  };

  const data = projectData[project];

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === data.slideImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? data.slideImages.length - 1 : prev - 1
    );
  };

  const seoTitle =
    project === "iplay"
      ? "I-Play: 인하대학교 I-Class 강의 자동 플레이어"
      : "I-Notice: 인하대학교 공지사항 알림 서비스";

  const seoDescription =
    project === "iplay"
      ? "인하대학교 I-Class 강의 자동 재생 및 수강완료 관리를 더욱 편리하게 만들어주는 Chrome 확장프로그램입니다."
      : "인하대학교의 중요한 공지사항을 놓치지 않도록 도와주는 통합 알림 서비스입니다.";

  return (
    <>
      <Helmet>
        <title>{seoTitle} | I-Projects</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>
      <motion.div
        className="project-detail-overlay"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 30,
        }}
      >
        <button className="back-button" onClick={onClose}>
          <FaArrowLeft /> 돌아가기
        </button>

        <div className="project-detail-content">
          <motion.div
            className="project-header"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="title-container">
              <img
                src={`/imgs/icons/I-${
                  project === "iplay" ? "Play" : "Notice"
                }.png`}
                alt={data.title}
                className="project-title-icon"
              />
              <h1 className="glowing-text">{data.title}</h1>
            </div>
            <p className="description">{data.description}</p>
          </motion.div>

          <Tilt
            className="tilt-container"
            tiltMaxAngleX={5}
            tiltMaxAngleY={5}
            scale={1.02}
            transitionSpeed={2000}
          >
            <motion.div
              className="project-main-image"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <img src={data.mainImage} alt={data.title} />
            </motion.div>
          </Tilt>

          <motion.div
            className="features-section"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2>주요 기능</h2>
            <div className="features-grid">
              {data.features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-item glass-effect"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  {feature}
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="slider-section"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <h2>사용 방법</h2>
            <div className="slider-container glass-effect">
              <button className="slider-button prev" onClick={prevSlide}>
                <FaChevronLeft />
              </button>
              <div className="slider-wrapper">
                <AnimatePresence
                  initial={false}
                  mode="wait"
                  custom={currentSlide}
                >
                  <motion.img
                    key={currentSlide}
                    src={data.slideImages[currentSlide]}
                    alt={`Step ${currentSlide + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
              </div>
              <button className="slider-button next" onClick={nextSlide}>
                <FaChevronRight />
              </button>
              <div className="slider-dots">
                {data.slideImages.map((_, index) => (
                  <button
                    key={index}
                    className={`slider-dot ${
                      currentSlide === index ? "active" : ""
                    }`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="download-section"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <h2>다운로드</h2>
            <div className="download-content">
              <motion.a
                href={data.downloadLinks.chrome}
                className="download-button chrome"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="download-icon">
                  <FaChrome />
                </div>
                <div className="download-text">
                  <span className="download-title">Chrome 웹 스토어</span>
                  <span className="download-subtitle">무료 설치하기</span>
                </div>
              </motion.a>

              {project === "iplay" && (
                <motion.a
                  href={data.downloadLinks.youtube}
                  className="download-button youtube"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="download-icon youtube">
                    <FaYoutube />
                  </div>
                  <div className="download-text">
                    <span className="download-title">사용 방법</span>
                    <span className="download-subtitle">영상으로 보기</span>
                  </div>
                </motion.a>
              )}
            </div>
          </motion.div>

          {data.additionalInfo && (
            <motion.div
              className="additional-info glass-effect"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <p>{data.additionalInfo}</p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default ProjectDetail;
